import React, { useMemo, useState } from "react";
import { Nav, Navbar, NavDropdown } from "react-bootstrap";
import { shallowEqual } from "react-redux";
import { groupChannelTypeList, OwnerAdminRoleList } from "../../../constants";
import { useChatsUIContext } from "../../../context/ChatsContext";
import { checkIsMuted, toggleMuteNotif } from "../../../functions";
import { useChatDispatch, useChatSelector } from "../../../hooks/reduxHook";
import { useChatNavigation } from "../../../hooks/useChatNavigation";
import useChatUtils from "../../../hooks/useChatUtils";
import {
  deleteConversation,
  leaveConversation,
} from "../../../store/chatActions";
import { NavDropdownUtilsStyled } from "../../../style";
import { ConversationTypeEnum } from "../../../types/enums";
import { useAppTranslation } from "../../../../../i18n/useAppTranslation";

const ChatUtils = () => {
  const { t } = useAppTranslation("common");
  const dispatch = useChatDispatch();
  const [chatNavbarShow, deleteLeaveButtonsShow, muteButtonShow] =
    useChatUtils();
  const { backToPrevRoute } = useChatNavigation();

  const [isMuted, setIsMuted] = useState<boolean>(false);

  const selectedChat = useChatSelector(
    (state) => state.chats.selectedChat,
    shallowEqual
  );

  const isAdmin = useChatSelector(
    (state: any) => state.chats.isAdmin,
    shallowEqual
  );

  const chatsContext = useChatsUIContext();
  const chatsUIProps = useMemo(() => {
    return {
      navigatePage: chatsContext?.navigatePage,
      setNavigatePage: chatsContext?.setNavigatePage,
      setConfirmDialog: chatsContext?.setConfirmDialog,
      setNewChatActionsShow: chatsContext?.setNewChatActionsShow,
    };
  }, [chatsContext?.navigatePage]);

  const handleNewChatActionPage = () => {
    if (isAdmin) chatsUIProps?.setNewChatActionsShow?.(true);
    else {
      const route = "explore-page";
      const history = chatsUIProps?.navigatePage?.history || [];
      const key = chatsUIProps?.navigatePage?.key || [];

      const duplicateHistory = history?.at(-1) === route;
      chatsUIProps?.setNavigatePage?.({
        current: route,
        key: duplicateHistory ? key : [...key, "home"],
        history: duplicateHistory ? history : [...history, route],
      });
    }
  };

  const handleChatDelete = () => {
    chatsUIProps?.setConfirmDialog?.({
      id: "delete-chat",
      open: true,
      submit: () => {
        if (deleteLeaveButtonsShow && selectedChat?.type) {
          dispatch(
            deleteConversation(selectedChat?._id, selectedChat?.type)
          ).then(() => backToPrevRoute());
          chatsUIProps?.setConfirmDialog?.((prev) => ({
            ...prev,
            open: false,
          }));
        }
      },
      title: t("chat.actions.deleteChat"),
      msg: t("chat.confirm.deleteChat"),
    });
  };

  const handleChatLeave = () => {
    chatsUIProps?.setConfirmDialog?.({
      id: "leave-chat",
      open: true,
      submit: () => {
        if (
          deleteLeaveButtonsShow &&
          groupChannelTypeList.includes(selectedChat?.type!)
        ) {
          dispatch(
            leaveConversation(selectedChat?._id, selectedChat?.type!)
          ).then(() => backToPrevRoute());
          chatsUIProps?.setConfirmDialog?.((prev) => ({
            ...prev,
            open: false,
          }));
        }
      },
      title: t("chat.actions.leaveChat"),
      msg: t("chat.confirm.leaveType", {
        type: selectedChat?.type,
      }),
    });
  };

  const handleChatUpdate = () => {
    const history = chatsUIProps?.navigatePage?.history || [];
    const key = chatsUIProps?.navigatePage?.key || [];

    chatsUIProps?.setNavigatePage?.({
      current: `create-${selectedChat?.type}`,
      key: [...key, "group"],
      history: [...history, selectedChat?.type!],
    });
  };

  const handleSubscribersList = () => {
    const history = chatsUIProps?.navigatePage?.history || [];
    const key = chatsUIProps?.navigatePage?.key || [];

    chatsUIProps?.setNavigatePage?.({
      current: "subscribers-list",
      key: [...key, "group"],
      history: [...history, "subscribers-list"],
    });
  };

  return (
    <Navbar expand="sm" className="bg-body-tertiary">
      <Nav className="d-flex flex-row gap-2">
        <Nav.Link key="action" className="p-0">
          {chatsUIProps?.navigatePage?.key.at(-1) === "home" && (
            <button
              className="border-0 p-0 bg-transparent"
              onClick={handleNewChatActionPage}
            >
              <img
                src="/assets/images/chat/svg/plus.svg"
                width={20}
                height={20}
                alt="plus"
              />
            </button>
          )}
        </Nav.Link>
        {chatNavbarShow && (
          <NavDropdownUtilsStyled
            id="collapsible-nav-dropdown"
            title={
              <img
                width={20}
                height={20}
                alt="menu"
                src="/assets/images/chat/svg/menu.svg"
              />
            }
            align="end"
            onClick={() => setIsMuted(checkIsMuted())}
          >
            {muteButtonShow && (
              <NavDropdown.Item key="Mute" onClick={toggleMuteNotif}>
                {isMuted ? t("chat.actions.unmute") : t("chat.actions.mute")}
              </NavDropdown.Item>
            )}
            {deleteLeaveButtonsShow && (
              <>
                {selectedChat?.type !== ConversationTypeEnum.PERSONAL && (
                  <>
                    {OwnerAdminRoleList.includes(
                      selectedChat?.membership?.role!
                    ) && (
                      <>
                        <NavDropdown.Item
                          key="update"
                          onClick={handleChatUpdate}
                        >
                          {t("chat.actions.updateGroup")}
                        </NavDropdown.Item>

                        <NavDropdown.Item
                          key="subscribers"
                          onClick={handleSubscribersList}
                        >
                          {t("chat.actions.subscribers")}
                        </NavDropdown.Item>
                      </>
                    )}
                    <NavDropdown.Item
                      className="text-danger"
                      key="delete"
                      onClick={handleChatLeave}
                    >
                      {t("chat.actions.leaveGroup")}
                    </NavDropdown.Item>
                  </>
                )}
                {(selectedChat?.type === ConversationTypeEnum.PERSONAL ||
                  OwnerAdminRoleList.includes(
                    selectedChat?.membership?.role!
                  )) && (
                  <NavDropdown.Item
                    className="text-danger text-capitalize"
                    key="delete"
                    onClick={handleChatDelete}
                  >
                    {t("chat.actions.deleteChat")}
                  </NavDropdown.Item>
                )}
              </>
            )}
          </NavDropdownUtilsStyled>
        )}
      </Nav>
    </Navbar>
  );
};

export default ChatUtils;
